import type React from 'react';
import { type ReactNode, createContext, useContext, useEffect, useMemo } from 'react';
import { COLORS as DEFAULT_COLORS } from '../constants/design';
import { useGlobalStore } from '../stores/globalStore';
import {
  type generateColorPalette,
  generateColorSystem,
  getBorderColors,
  getMenuStateColors,
  getScrollbarColors,
  getSurfaceColors,
  getTextColors,
} from '../utils/colorUtils';

// Helper to add semantic names to static color palettes
function addSemanticToStatic<T extends Record<string | number, string>>(palette: T) {
  return {
    ...palette,
    lighter: palette[50] || palette['50'],
    light: palette[200] || palette['200'],
    main: palette[500] || palette['500'],
    dark: palette[600] || palette['600'],
    darker: palette[700] || palette['700'],
    contrastText: '#ffffff',
  } as T & {
    lighter: string;
    light: string;
    main: string;
    dark: string;
    darker: string;
    contrastText: string;
  };
}

interface ThemeColors {
  primary: ReturnType<typeof generateColorPalette>;
  secondary: ReturnType<typeof generateColorPalette>;
  tertiary: ReturnType<typeof generateColorPalette>;
  neutral: ReturnType<typeof generateColorSystem>['neutral'];
  gray: typeof DEFAULT_COLORS.gray;
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    disabled: string;
  };
  border: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  surface: {
    container: string;
    layout: string;
    elevated: string;
    hover: string;
  };
  menu: {
    hover: string;
    selected: string;
    selectedBar: string;
    selectedColor: string;
  };
  // Semantic colors with semantic names
  success: ReturnType<typeof addSemanticToStatic<typeof DEFAULT_COLORS.success>>;
  warning: ReturnType<typeof addSemanticToStatic<typeof DEFAULT_COLORS.warning>>;
  error: ReturnType<typeof addSemanticToStatic<typeof DEFAULT_COLORS.error>>;
  info: ReturnType<typeof addSemanticToStatic<typeof DEFAULT_COLORS.info>>;
}

interface ThemeContextType {
  colors: ThemeColors;
  theme: 'light' | 'dark';
  primaryColorKey: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { theme, customization } = useGlobalStore();

  // Determine which color palette to use
  const primaryColorKey = customization.primaryColor || 'blue';
  let colorKey = 'blue';

  // Map hex colors to color keys for backward compatibility
  const colorMap: Record<string, string> = {
    '#3b82f6': 'blue',
    '#10b981': 'green',
    '#ef4444': 'red',
    '#f59e0b': 'yellow',
    '#8b5cf6': 'purple',
    '#ec4899': 'pink',
    '#6366f1': 'indigo',
    '#14b8a6': 'teal',
  };

  if (typeof primaryColorKey === 'string') {
    if (primaryColorKey.startsWith('#')) {
      // It's a hex color, map it to a color key
      colorKey = colorMap[primaryColorKey] || 'blue';
    } else {
      // It's already a color key
      colorKey = primaryColorKey;
    }
  }

  // Generate the complete color system
  const colorSystem = useMemo(() => generateColorSystem(colorKey), [colorKey]);
  const textColors = useMemo(() => getTextColors(theme), [theme]);
  const borderColors = useMemo(() => getBorderColors(theme), [theme]);
  const surfaceColors = useMemo(() => getSurfaceColors(theme), [theme]);
  const menuStateColors = useMemo(
    () =>
      getMenuStateColors(
        colorSystem.primary.main,
        colorSystem.primary.lighter,
        theme,
        colorSystem.primary.dark,
      ),
    [colorSystem.primary.main, colorSystem.primary.lighter, theme, colorSystem.primary.dark],
  );

  // Create the complete color theme with semantic tokens
  const colors: ThemeColors = useMemo(
    () => ({
      primary: colorSystem.primary,
      secondary: colorSystem.secondary,
      tertiary: colorSystem.tertiary,
      neutral: colorSystem.neutral,
      gray: DEFAULT_COLORS.gray,
      text: textColors,
      border: borderColors,
      surface: surfaceColors,
      menu: menuStateColors,
      // Semantic colors with semantic names added
      success: addSemanticToStatic(DEFAULT_COLORS.success),
      warning: addSemanticToStatic(DEFAULT_COLORS.warning),
      error: addSemanticToStatic(DEFAULT_COLORS.error),
      info: addSemanticToStatic(DEFAULT_COLORS.info),
    }),
    [colorSystem, textColors, borderColors, surfaceColors, menuStateColors],
  );

  // Expose design tokens as CSS variables for CSS to use (themeable, no hardcoded colors)
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--prakriya-menu-item-hover-bg',
      menuStateColors.hover,
    );
    document.documentElement.style.setProperty(
      '--prakriya-menu-item-selected-bg',
      menuStateColors.selected,
    );
    document.documentElement.style.setProperty(
      '--prakriya-menu-item-selected-bar',
      menuStateColors.selectedBar,
    );
    document.documentElement.style.setProperty(
      '--prakriya-menu-item-selected-color',
      menuStateColors.selectedColor,
    );

    // Scrollbar: Ant Design has no scrollbar tokens; we drive track/thumb from theme
    const scrollbar = getScrollbarColors(theme);
    document.documentElement.style.setProperty('--prakriya-scrollbar-track', scrollbar.track);
    document.documentElement.style.setProperty('--prakriya-scrollbar-thumb', scrollbar.thumb);
    document.documentElement.style.setProperty(
      '--prakriya-scrollbar-thumb-hover',
      scrollbar.thumbHover,
    );
  }, [
    theme,
    menuStateColors.hover,
    menuStateColors.selected,
    menuStateColors.selectedBar,
    menuStateColors.selectedColor,
  ]);

  return (
    <ThemeContext.Provider value={{ colors, theme, primaryColorKey: colorKey }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook to get colors with theme support
export const useColors = () => {
  const { colors } = useTheme();
  return colors;
};
